import { Box, Divider, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { TextInput } from '@/components/form/baseInputs/TextInput/TextInput';
import { Copy01Icon } from '@/components/icons/Copy01Icon';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { ManagedSftpConnectionStatus } from '@/types/schema';

import {
  AdminSecurityPage_ManagedSftpConnectionFragment as ManagedSFTPConnection,
  AdminSecurityPage_ManagedSftpPublicKeyFragment as ManagedSFTPPublicKey,
} from '../AdminSecurityPage/graphql/ManagedSFTP.generated';

function CopyableValue({ label, value }: { label: string; value: string }) {
  const [copyToClipboard] = useCopyToClipboard();
  const { createErrorFeedback, createSuccessFeedback } = useFeedback();

  return (
    <Stack spacing={0.5} minWidth={0}>
      <Typography variant="title-xs">{label}</Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          component="code"
          sx={{ fontSize: 13, overflowWrap: 'anywhere', userSelect: 'all' }}
        >
          {value}
        </Box>
        <Button
          aria-label={`Copy ${label.toLowerCase()}`}
          size="sm"
          variant="transparent"
          startIcon={Copy01Icon}
          onClick={() =>
            copyToClipboard(
              value,
              createSuccessFeedback(`${label} copied`),
              createErrorFeedback(`Failed to copy ${label.toLowerCase()}`)
            )
          }
        >
          Copy
        </Button>
      </Stack>
    </Stack>
  );
}

function PublicKeyRow({
  publicKey,
  removing,
  onRemove,
}: {
  publicKey: ManagedSFTPPublicKey;
  removing: boolean;
  onRemove: () => void;
}) {
  return (
    <Stack spacing={1.5} py={2} data-testid={`sftp-key-${publicKey.id}`}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Stack spacing={0.5} minWidth={0}>
          <Typography variant="title-xs">{publicKey.algorithm} key</Typography>
          <Typography
            component="code"
            variant="body-xs"
            sx={{ overflowWrap: 'anywhere' }}
          >
            {publicKey.fingerprintSHA256}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {/* The live query returns only installed keys, not revoked history. */}
          <Badge variant={BadgeVariants.Success} display="Active" />
          <Button
            aria-label="Remove"
            size="sm"
            variant="secondary"
            loading={removing}
            onClick={onRemove}
          >
            Remove
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}

interface ManagedSFTPSettingsContentProps {
  connection: ManagedSFTPConnection | null;
  filesHref?: string;
  enableLoading: boolean;
  mutationLoading: boolean;
  onEnable: () => void;
  onAddKey: (publicKeyText: string) => Promise<boolean>;
  onRemoveKey: (publicKey: ManagedSFTPPublicKey) => void;
  onDisable: () => void;
}

export function ManagedSFTPSettingsContent({
  connection,
  filesHref,
  enableLoading,
  mutationLoading,
  onEnable,
  onAddKey,
  onRemoveKey,
  onDisable,
}: ManagedSFTPSettingsContentProps) {
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [publicKeyText, setPublicKeyText] = useState('');
  const [publicKeyError, setPublicKeyError] = useState<string>();
  const enabled = connection?.status === ManagedSftpConnectionStatus.Enabled;
  const installedKeyCount = connection?.publicKeys.length ?? 0;
  const viewFilesAction = filesHref && (
    <Button
      size="sm"
      variant="secondary"
      href={filesHref}
      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
    >
      View files
    </Button>
  );

  if (!enabled) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <Stack spacing={0.5}>
          <Typography variant="title-l">Managed SFTP</Typography>
          <Typography variant="body-s">
            Securely transfer files to Luminary with your organization&apos;s
            SSH public keys.
          </Typography>
        </Stack>
        <Badge
          variant={BadgeVariants.SecondaryFilled}
          display={connection ? 'Disabled' : 'Not enabled'}
        />
        {connection && (
          <Typography variant="body-xs">
            Enable SFTP, then register your SSH public keys again. Previous keys
            are not restored.
          </Typography>
        )}
        <Button
          size="sm"
          variant="primary"
          loading={enableLoading}
          onClick={onEnable}
        >
          Enable SFTP
        </Button>
      </Stack>
    );
  }

  const handleAddKey = async () => {
    if (!publicKeyText.trim()) {
      setPublicKeyError('Enter one supported OpenSSH public key.');
      return;
    }

    setPublicKeyError(undefined);
    if (await onAddKey(publicKeyText.trim())) {
      setPublicKeyText('');
      setShowKeyForm(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Stack spacing={0.5}>
          <Typography variant="title-l">Managed SFTP</Typography>
          <Typography variant="body-s">
            Use these connection details to securely transfer files.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <Badge variant={BadgeVariants.Success} display="Enabled" />
          {viewFilesAction}
        </Stack>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={3}
        useFlexGap
        flexWrap="wrap"
      >
        <CopyableValue
          label="Endpoint"
          value={`${connection.instructions.hostname}:${connection.instructions.port}`}
        />
        <CopyableValue label="Username" value={connection.username} />
      </Stack>

      <Stack spacing={1}>
        <Typography variant="title-xs">Host key fingerprint</Typography>
        {connection.instructions.hostKeyFingerprints.map(({ fingerprint }) => (
          <Typography
            key={fingerprint}
            component="code"
            variant="body-xs"
            sx={{ overflowWrap: 'anywhere' }}
          >
            {fingerprint}
          </Typography>
        ))}
      </Stack>

      <Divider />

      <Stack spacing={1}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="title-s">SSH public keys</Typography>
          {!showKeyForm && installedKeyCount === 0 && (
            <Button
              size="sm"
              variant="secondary"
              disabled={mutationLoading}
              onClick={() => setShowKeyForm(true)}
            >
              Add key
            </Button>
          )}
        </Stack>

        {showKeyForm && installedKeyCount === 0 && (
          <Stack spacing={1.5} pt={1}>
            <TextInput
              label="SSH public key"
              value={publicKeyText}
              onChange={(event) => setPublicKeyText(event.target.value)}
              errorMessage={publicKeyError}
              helpText="Paste one RSA (2048-bit or stronger), ECDSA, or Ed25519 OpenSSH public key."
              multiline
              minRows={4}
              autoFocus
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="sm"
                variant="secondary"
                disabled={mutationLoading}
                onClick={() => {
                  setPublicKeyText('');
                  setPublicKeyError(undefined);
                  setShowKeyForm(false);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="primary"
                loading={mutationLoading}
                onClick={handleAddKey}
              >
                Add key
              </Button>
            </Stack>
          </Stack>
        )}

        {connection.publicKeys.length === 0 ? (
          <Typography variant="body-xs" sx={{ py: 2 }}>
            No SSH public keys have been added.
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {connection.publicKeys.map((publicKey) => (
              <PublicKeyRow
                key={publicKey.id}
                publicKey={publicKey}
                removing={mutationLoading}
                onRemove={() => onRemoveKey(publicKey)}
              />
            ))}
          </Stack>
        )}
      </Stack>

      <Callout severity="info-low">
        Disabling SFTP removes all registered keys and blocks future logins. It
        does not end sessions that are already connected. If you enable SFTP
        again, register your keys again.
      </Callout>
      <Stack direction="row" justifyContent="flex-end">
        <Button
          size="sm"
          variant="destructive-secondary"
          disabled={mutationLoading}
          onClick={onDisable}
        >
          Disable SFTP
        </Button>
      </Stack>
    </Stack>
  );
}
